import React, {Component} from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import moment from 'moment';
import Image from 'react-bootstrap/Image';
import Card from 'react-bootstrap/Card';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import i18n from 'i18next';
import Iframe from 'react-iframe';

import { fullCalendarLocale, fullCalendarDirection } from '../localeUi.js';
import { trackEvent } from '../analytics.js';
import GetYourGuideWidget from './GetYourGuideWidget';
import DestinationInsights from './DestinationInsights';
import TravelataAccommodation from './TravelataAccommodation';
import { formatPlaceLabel, handleTravelImageError } from '../utils/travelDisplay.js';
import './TravelItem.css';
import './TravelDetailPanel.css';

const STAY22_AID = 'graverint';
const GYG_PARTNER_ID = '1J6JKRV';

function mapI18nLangToGygLocale(lang) {
  const base = (lang || 'en').split('-')[0];
  const map = {
    en: 'en-US',
    ru: 'ru-RU',
    de: 'de-DE',
    fr: 'fr-FR',
    it: 'it-IT',
    es: 'es-ES',
    ja: 'ja-JP',
    zh: 'zh-CN',
    ar: 'ar-SA',
    he: 'en-US'
  };
  return map[base] || 'en-US';
}

function buildGygSearchUrl(query) {
  return `https://www.getyourguide.com/s/?q=${encodeURIComponent(query)}&partner_id=${GYG_PARTNER_ID}`;
}

class TravelDetailPanel extends Component {
  detailCalendarRef = React.createRef();
  detailCalendarHostRef = React.createRef();
  detailCalendarResizeObserver = null;
  detailCalendarResizeTimers = [];
  stay22Root = null;

  state = {
    mapActive: false,
  };

  componentDidMount() {
    this.scheduleDetailCalendarResize();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.travel?.id !== this.props.travel?.id) {
      this.unmountStay22();
      this.setState({ mapActive: false });
      this.scheduleDetailCalendarResize();
    }
  }

  componentWillUnmount() {
    this.disposeDetailCalendarResizeWatchers();
    this.unmountStay22();
  }

  unmountStay22 = () => {
    if (this.stay22Root) {
      this.stay22Root.unmount();
      this.stay22Root = null;
    }
  };

  disposeDetailCalendarResizeWatchers = () => {
    if (this.detailCalendarResizeObserver) {
      this.detailCalendarResizeObserver.disconnect();
      this.detailCalendarResizeObserver = null;
    }
    this.detailCalendarResizeTimers.forEach((id) => clearTimeout(id));
    this.detailCalendarResizeTimers = [];
  };

  scheduleDetailCalendarResize = () => {
    this.disposeDetailCalendarResizeWatchers();

    const run = () => {
      const api = this.detailCalendarRef.current?.getApi?.();
      api?.updateSize();
    };

    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });

    const collapseMs = typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : 320;
    this.detailCalendarResizeTimers = [0, 50, collapseMs].map((ms) => setTimeout(run, ms));

    const host = this.detailCalendarHostRef.current;
    if (host && typeof ResizeObserver !== 'undefined') {
      this.detailCalendarResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(run);
      });
      this.detailCalendarResizeObserver.observe(host);
    }
  };

  activateMap = () => {
    this.setState({ mapActive: true }, () => {
      this.loadStay22Embed();
    });
  };

  loadStay22Embed = () => {
    const { travel } = this.props;
    if (!travel?.city) {
      return;
    }
    const city = travel.city.name_translations.en;
    const country = travel.country;
    const calStart = moment(travel.depart_date).format('YYYY-MM-DD');
    const calEnd = moment(travel.return_date).format('YYYY-MM-DD');
    const iframeId = 'if-panel-' + travel.id;
    const stay22ID = 'stay22-panel-' + travel.id;
    const placeLabel = formatPlaceLabel(city, country || '');
    const stay22Address = encodeURIComponent(placeLabel);
    const stay22URL =
      `https://www.stay22.com/embed/gm?aid=${STAY22_AID}&address=${stay22Address}&checkin=${calStart}&checkout=${calEnd}`;

    const container = document.getElementById(iframeId);
    if (!container) {
      return;
    }
    this.unmountStay22();
    this.stay22Root = createRoot(container);
    this.stay22Root.render(
      <Iframe url={stay22URL}
              width="100%"
              loading="lazy"
              id={stay22ID}
              className="stay22"
              display="initial"
              frameBorder={0}
              height="400px"
              scrolling="no"
              position="relative"/>
    );
  };

  renderBlock1(t, ctx) {
    const {
      travel, placeLabel, image, departDate, returnDate, days,
      priceStr, urlFrom, airportName, onBook,
    } = ctx;

    return (
      <div className="travel-detail-panel__block1">
        <div className="travel-detail-panel__hero">
          <Image src={image} className="card-img-top card-image travel-item-card__image"
                 alt={placeLabel} onError={handleTravelImageError} />
        </div>
        <div className="travel-item-card__meta">
          <span className="travel-item-card__dates">{departDate} → {returnDate}</span>
          <span className="travel-item-card__pill">{t('travelCard.duration', { count: days })}</span>
        </div>
        <div className="travel-detail-panel__summary travel-item-card__body">
          <div className="travel-item-card__main">
            <div className="travel-item-card__place-block">
              <div className="travel-item-card__place">{placeLabel}</div>
              <a
                className="travel-item-card__alt-origin"
                href={urlFrom}
                aria-label={t('travelCard.dealsFromCodeAria', {
                  airport: airportName,
                  code: travel.airport.code,
                })}
                title={t('travelCard.dealsFromCodeAria', {
                  airport: airportName,
                  code: travel.airport.code,
                })}
              >
                {t('travelCard.dealsFromCode', { code: travel.airport.code })}
              </a>
            </div>
            <div className="travel-item-card__price-block"
                 aria-label={t('travelCard.priceAria', { price: priceStr })}>
              <div className="travel-item-card__price-stack">
                <span className="travel-item-card__price">{priceStr}</span>
                <span className="travel-item-card__price-hint">{t('travelCard.priceHint')}</span>
              </div>
            </div>
          </div>
          <button type="button"
                  className="btn travel-item-card__cta travel-item-card__cta--primary travel-detail-panel__book"
                  title={t('travelCard.bookFor', { price: priceStr })}
                  onClick={() => onBook(travel)}>
            {t('travelCard.bookNow')}
          </button>
          <DestinationInsights travel={travel} />
        </div>
      </div>
    );
  }

  renderCalendar(t, calendarID, events, travel) {
    return (
      <div className="travel-detail-panel__calendar-block">
        <div className="travel-item-card__detail-section item-calendar">
          <span className="text-muted">{t('travelCard.calendarHeading')}</span>
          <div ref={this.detailCalendarHostRef} className="item-calendar__host">
            <FullCalendar
              ref={this.detailCalendarRef}
              initialView="dayGridMonth"
              locales={allLocales}
              locale={fullCalendarLocale(i18n.language)}
              direction={fullCalendarDirection(i18n.language)}
              height="auto"
              fixedWeekCount={false}
              plugins={[dayGridPlugin, interactionPlugin]}
              weekends={true}
              initialDate={travel.depart_date}
              events={events}
              id={calendarID}
            />
          </div>
        </div>
      </div>
    );
  }

  renderMapColumn(t, iframeId, mapActive, showHeading = true) {
    return (
      <div className="travel-detail-panel__accommodation-col travel-detail-panel__accommodation-col--map">
        {showHeading && (
          <>
            <h3 className="travel-detail-panel__accommodation-subtitle">
              {t('travelCard.mapColumnHeading')}
            </h3>
            <p className="travel-detail-panel__accommodation-lead">{t('travelCard.mapColumnLead')}</p>
          </>
        )}
        <div className="travel-detail-panel__map-host">
          {mapActive ? (
            <div
              id={iframeId}
              className="travel-item-card__embed-host travel-detail-panel__map-active"
            />
          ) : (
            <button
              type="button"
              className="travel-detail-panel__map-blocked"
              onClick={this.activateMap}
              aria-label={t('travelCard.showMap')}
            >
              <div className="travel-detail-panel__map-blocked-preview" aria-hidden="true" />
              <div className="travel-detail-panel__map-blocked-overlay">
                <span className="travel-detail-panel__map-blocked-icon" aria-hidden="true">📍</span>
                <strong>{t('travelCard.showMap')}</strong>
                <span>{t('travelCard.mapBlockedHint')}</span>
              </div>
            </button>
          )}
        </div>
      </div>
    );
  }

  renderTravelataReferral(t, searchPageUrl, travel) {
    if (!searchPageUrl) {
      return null;
    }
    return (
      <a
        className="travel-detail-panel__travelata-referral"
        href={searchPageUrl}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={() => {
          trackEvent('travelata_search_click', {
            destination_iata: travel.destination,
            source: 'referral_fallback',
          });
        }}
      >
        {t('travelCard.travelataReferral')}
      </a>
    );
  }

  render() {
    const t = i18n.t.bind(i18n);
    const { travel, onBook, onBack, lngPrefix = '' } = this.props;

    if (!travel || !travel.city) {
      return (
        <div className="travel-detail-panel travel-detail-panel--empty">
          <p className="text-muted mb-0">{t('travelCard.selectPrompt')}</p>
        </div>
      );
    }

    const country = travel.country || '';
    const city = travel.city.name_translations.en;
    const placeLabel = formatPlaceLabel(city, country);
    const airportName = travel.airport.name_translations.en;
    const image = travel.image_big;
    const departDate = moment(travel.depart_date).format('DD/MM/YYYY');
    const returnDate = moment(travel.return_date).format('DD/MM/YYYY');
    const calStart = moment(travel.depart_date).format('YYYY-MM-DD');
    const calEnd = moment(travel.return_date).format('YYYY-MM-DD');
    const days = moment(travel.return_date).diff(moment(travel.depart_date), 'days');
    const priceStr = `$${travel.value}`;
    const urlFrom = `${lngPrefix}/from/${travel.airport.code}/since/${calStart}`;
    const calendarID = 'cal-panel-' + travel.id;
    const iframeId = 'if-panel-' + travel.id;
    const events = [{
      id: travel.id,
      title: `${placeLabel} $${travel.value}`,
      start: travel.depart_date,
      end: travel.return_date,
      className: 'bg-success text-white',
      travel: travel
    }];
    const gygQuery = country ? `${city} ${country}` : city;
    const gygLocale = mapI18nLangToGygLocale(i18n.language);
    const gygMoreUrl = buildGygSearchUrl(gygQuery);
    const viatorUrl =
      `https://www.viator.com/searchResults/all?text=${encodeURIComponent(gygQuery)}&startDateFrom=${calStart}&startDateTo=${calEnd}`;
    const { mapActive } = this.state;

    const block1Ctx = {
      travel, placeLabel, image, departDate, returnDate, days,
      priceStr, urlFrom, airportName, onBook,
    };

    return (
      <Card className="travel-detail-panel card travel-item-card travel-item-card--panel">
        {onBack && (
          <button type="button" className="travel-detail-panel__back" onClick={onBack}>
            ← {t('travelCard.backToDeals')}
          </button>
        )}

        <div className="travel-detail-panel__top-row">
          {this.renderBlock1(t, block1Ctx)}
          {this.renderCalendar(t, calendarID, events, travel)}
        </div>

        <section className="travel-detail-panel__section travel-detail-panel__activities">
          <span className="text-muted travel-detail-panel__section-title">
            {t('travelCard.gygWidgetHeading')}
          </span>
          <GetYourGuideWidget
            partnerId={GYG_PARTNER_ID}
            query={gygQuery}
            localeCode={gygLocale}
            dateFrom={calStart}
            dateTo={calEnd}
            moreHref={gygMoreUrl}
            contextText={t('travelCard.gygWidgetContext', {
              city,
              country,
              dateFrom: calStart,
              dateTo: calEnd,
            })}
            onMoreClick={() => {
              trackEvent('gyg_click', {
                destination_iata: travel.destination,
                city,
              });
            }}
          />
          <div className="travel-item-card__things-actions">
            <a className="btn travel-item-card__link-btn travel-item-card__link-btn--viator"
               href={viatorUrl}
               target="_blank"
               rel="noreferrer"
               onClick={() => {
                 trackEvent('viator_click', {
                   destination_iata: travel.destination,
                   city,
                 });
               }}>
              {t('travelCard.thingsViator')}
            </a>
          </div>
        </section>

        <section className="travel-detail-panel__section travel-detail-panel__accommodation">
          <span className="text-muted travel-detail-panel__section-title">
            {t('travelCard.destinationHeading')}
          </span>
          <TravelataAccommodation travel={travel}>
            {({ loading, hasTours, searchPageUrl, list }) => {
              if (loading) {
                return (
                  <div className="travel-detail-panel__accommodation-grid">
                    <div className="travel-detail-panel__accommodation-col travel-detail-panel__accommodation-col--packages">
                      <h3 className="travel-detail-panel__accommodation-subtitle">
                        {t('travelCard.travelataColumnHeading')}
                      </h3>
                      <p className="travelata-accommodation__status travel-detail-panel__accommodation-loading">
                        {t('travelCard.travelataLoading')}
                      </p>
                    </div>
                    {this.renderMapColumn(t, iframeId, mapActive)}
                  </div>
                );
              }

              if (hasTours) {
                return (
                  <div className="travel-detail-panel__accommodation-grid">
                    <div className="travel-detail-panel__accommodation-col travel-detail-panel__accommodation-col--packages">
                      <h3 className="travel-detail-panel__accommodation-subtitle">
                        {t('travelCard.travelataColumnHeading')}
                      </h3>
                      <p className="travel-detail-panel__accommodation-lead">{t('travelCard.accommodationLead')}</p>
                      {list}
                    </div>
                    {this.renderMapColumn(t, iframeId, mapActive)}
                  </div>
                );
              }

              return (
                <div className="travel-detail-panel__accommodation-fallback">
                  <p className="travel-detail-panel__accommodation-lead">{t('travelCard.mapColumnLead')}</p>
                  {this.renderMapColumn(t, iframeId, mapActive, false)}
                  {this.renderTravelataReferral(t, searchPageUrl, travel)}
                </div>
              );
            }}
          </TravelataAccommodation>
        </section>
      </Card>
    );
  }
}

TravelDetailPanel.propTypes = {
  travel: PropTypes.object,
  onBook: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  lngPrefix: PropTypes.string,
};

export default TravelDetailPanel;
